/**
 * Heuristics for document-extracted image artifacts shown in the source preview.
 *
 * Large-dimension limits mirror backend Bedrock vision prep
 * (`backend/utils/llm_image.py` → `BEDROCK_MAX_IMAGE_DIMENSION = 8000`).
 */

export type ExtractedImagePreviewIssue = 'large_dimensions' | 'low_resolution' | 'large_file'

/** Max width or height above which Bedrock vision downscales (see BEDROCK_MAX_IMAGE_DIMENSION). */
const WARN_MAX_EDGE_PX = 8000
/** Total megapixels above which in-browser decode/zoom can feel heavy (independent of max-edge). */
const WARN_MEGAPIXELS = 18
/** Short edge below this suggests a low-resolution raster extraction. */
const WARN_MIN_SHORT_EDGE_PX = 440
/** Area below this suggests limited detail for diagrams and screenshots. */
const WARN_MIN_AREA_PX = 200_000
/** Vision payloads above ~5 MB are commonly rejected or slow to process. */
const WARN_FILE_BYTES = 5 * 1024 * 1024

function megapixels(width: number, height: number): number {
	return (width * height) / 1_000_000
}

/**
 * Returns quality / size issues for an extracted (system) image, in stable display order.
 */
export function getExtractedImagePreviewIssues(
	width: number,
	height: number,
	fileSizeBytes: number
): ExtractedImagePreviewIssue[] {
	const issues: ExtractedImagePreviewIssue[] = []
	const maxEdge = Math.max(width, height)
	const minEdge = Math.min(width, height)
	const area = width * height

	if (maxEdge > WARN_MAX_EDGE_PX || megapixels(width, height) > WARN_MEGAPIXELS) {
		issues.push('large_dimensions')
	}
	if (minEdge < WARN_MIN_SHORT_EDGE_PX || area < WARN_MIN_AREA_PX) {
		issues.push('low_resolution')
	}
	if (fileSizeBytes >= WARN_FILE_BYTES) {
		issues.push('large_file')
	}
	return issues
}
